import React, { useState } from "react";
import apiClient from "../services/apiClient";

/**
 * Debugging component to trace address information by trace ID.
 */
const Debugging: React.FC = () => {
    const [traceId, setTraceId] = useState<string>("");
    const [secretKey, setSecretKey] = useState<string>("20bettertrucks25");
    const [response, setResponse] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const deepParse = (obj: any): any => {
        if (typeof obj !== 'object' || obj === null) {
            if (typeof obj === 'string') {
                try {
                    const parsed = JSON.parse(obj);
                    // If parsing results in an object or array, parse it deeply too
                    if (typeof parsed === 'object' && parsed !== null) {
                        return deepParse(parsed);
                    }
                    return parsed;
                } catch (e) {
                    return obj;
                }
            }
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(deepParse);
        }

        const newObj: any = {};
        for (const [key, value] of Object.entries(obj)) {
            newObj[key] = deepParse(value);
        }
        return newObj;
    };

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!traceId.trim()) return;

        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const url = `api/v1/helpers/address-info/${traceId.trim()}?secretKey=${secretKey.trim()}`;
            const res = await apiClient.get(url, {
                headers: {
                    'accept': '*/*'
                }
            });

            // Deeply parse the response to handle stringified JSON fields
            const parsedData = deepParse(res.data);
            setResponse(parsedData);
        } catch (err: any) {
            console.error("Error fetching address info:", err);
            setError(err.response?.data?.message || err.message || "An error occurred while fetching the data.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-content">
            <div className="container-fluid px-1 py-1">
                <div className="row mb-1">
                    <div className="col">
                        <h2 className="fw-bold mb-1">Debugging</h2>
                        <p className="text-muted">Trace address information using a Trace ID</p>
                    </div>
                </div>

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                        <form onSubmit={handleSearch} className="row g-3">
                            <div className="col-md-3">
                                <label htmlFor="secretKey" className="form-label fw-medium mb-1">Secret Key</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="bi bi-key"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        id="secretKey"
                                        placeholder="Secret Key"
                                        value={secretKey}
                                        onChange={(e) => setSecretKey(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-9">
                                <label htmlFor="traceId" className="form-label fw-medium mb-1">Address Trace ID</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="bi bi-hash"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        id="traceId"
                                        placeholder="Enter Trace ID (e.g. 4cd1aa17-ad25-48b5-af6f-0625cae04b93)"
                                        value={traceId}
                                        onChange={(e) => setTraceId(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={loading || !traceId.trim() || !secretKey.trim()}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-search me-2"></i> Search
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger border-0 shadow-sm d-flex align-items-center" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                        <div>{error}</div>
                    </div>
                )}

                {response && (
                    <div className="card border-0 shadow-sm overflow-hidden fadeIn">
                        <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold text-primary">Response Data</h5>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(response, null, 2));
                                }}
                            >
                                <i className="bi bi-clipboard me-1"></i> Copy JSON
                            </button>
                        </div>
                        <div className="card-body p-0">
                            <pre className="m-0 p-1 bg-light" style={{ maxHeight: '600px', overflowY: 'auto', fontSize: '13px' }}>
                                {JSON.stringify(response, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}

                {!response && !error && !loading && (
                    <div className="text-center py-5">
                        <i className="bi bi-search text-light" style={{ fontSize: '80px' }}></i>
                        <h4 className="text-muted mt-3">Enter a Trace ID to see the results</h4>
                        <p className="text-secondary">Information will be displayed here after a successful search.</p>
                    </div>
                )}
            </div>

            <style>{`
                .fadeIn {
                    animation: fadeIn 0.3s ease-in-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default Debugging;
